import React, { useEffect, useMemo } from "react";
import {
  alpha,
  Badge,
  Box,
  Chip,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
} from "@mui/material";
import GenieLogo from "../genie-logo/GenieLogo";
import { DRAWER_CONFIG } from "./config";
import GenieText from "../../core/genie-text/GenieText";
import GenieProfile from "../genie-login-button/GenieProfile";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Help, Store } from "@mui/icons-material";
import { theme } from "../genie-theme/genie-theme";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import GenieSpinner from "../../core/genie-spinner/GenieSpinner";
import useUser from "../../../queries/routes/useUser";
import useOnboardingStatus from "../../../queries/routes/useOnboardingStatus";
import * as FullStory from "@fullstory/browser";

export const DRAWER_WIDTH = 220;

const ONBOARDING_ITEMS = 5;

function GenieDrawer() {
  let { org_id } = useParams();
  const location = useLocation();
  const { data: user } = useUser();
  const { data: onboardingStatus } = useOnboardingStatus(org_id!);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      FullStory.identify(user.id, { email: user.email });
    }
  }, [user]);

  const onboardingStatusCount = useMemo(() => {
    let counter = 0;
    if (onboardingStatus) {
      if (
        onboardingStatus.has_description &&
        onboardingStatus.has_splash_image
      ) {
        counter++;
      }
      if (onboardingStatus.has_payment_info && onboardingStatus.has_address) {
        counter++;
      }
      if (onboardingStatus.product_count > 0) {
        counter++;
      }
      if (onboardingStatus.has_wholesale_terms) {
        counter++;
      }
      if (onboardingStatus.has_customers) {
        counter++;
      }
      return ONBOARDING_ITEMS - counter;
    } else return ONBOARDING_ITEMS;
  }, [onboardingStatus]);

  return (
    <Box display={"flex"}>
      <Drawer variant={"permanent"} sx={{ width: DRAWER_WIDTH }}>
        <Box
          py={3}
          px={2}
          width={DRAWER_WIDTH}
          display={"flex"}
          height={"100%"}
          flexDirection={"column"}
          justifyContent={"space-between"}
        >
          <Box>
            <GenieLogo />
            {user ? (
              <>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel size={"small"} id="org-id-select-label">
                    Organization
                  </InputLabel>
                  <Select
                    size={"small"}
                    labelId="org-id-select-label"
                    id="org-id-select"
                    value={org_id}
                    label="Organization"
                    onChange={(event) => {
                      localStorage.setItem("org", event.target.value);
                      navigate(`/app/${event.target.value}/orders`);
                    }}
                  >
                    {user &&
                      user.organizations &&
                      user?.organizations.map((org) => {
                        return (
                          <MenuItem key={org.id} value={org.id}>
                            {org.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
                <Box
                  mt={2}
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <GenieText
                    fontSize={"tiny"}
                    link={`/app/${org_id}/welcome`}
                    uppercase
                  >
                    Configure
                  </GenieText>
                  {onboardingStatus && onboardingStatusCount > 0 && (
                    <Badge
                      color={"secondary"}
                      badgeContent={onboardingStatusCount}
                    >
                      <Help fontSize={"small"} color="warning" />
                    </Badge>
                  )}
                  {onboardingStatus && onboardingStatusCount === 0 && (
                    <Chip
                      size={"small"}
                      variant={"outlined"}
                      color={"primary"}
                      label={"Ready!"}
                    />
                  )}
                </Box>
                <Box mt={3}>
                  {DRAWER_CONFIG.map(({ label, route }) => {
                    const isSelected =
                      location.pathname === `/app/${org_id}/${route}`;
                    return (
                      <Box
                        key={label}
                        p={1}
                        borderRadius={theme.shape.borderRadius}
                        sx={{
                          transition: "background-color 0.2s ease-in-out",
                          borderTopLeftRadius: 2,
                          borderBottomLeftRadius: 2,
                        }}
                        bgcolor={
                          isSelected
                            ? alpha(theme.palette.primary.light, 0.2)
                            : theme.palette.common.white
                        }
                      >
                        <GenieText
                          fontSize={"small"}
                          link={`/app/${org_id}/${route}`}
                        >
                          {label}
                        </GenieText>
                      </Box>
                    );
                  })}
                  <Box
                    py={1}
                    display={"flex"}
                    alignItems={"center"}
                    color={theme.palette.primary.main}
                  >
                    <GenieText
                      fontSize={"small"}
                      link={`/storefront/${org_id}`}
                    >
                      View as Buyer
                    </GenieText>
                    <Box ml={1}>
                      <Store />
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              <Box mt={6}>
                <Skeleton height={40} />
                {DRAWER_CONFIG.map((item) => (
                  <Skeleton key={item.label} height={40} />
                ))}
              </Box>
            )}
          </Box>
          <Box>
            <GenieProfile />
          </Box>
        </Box>
      </Drawer>
      {user && <Outlet />}
    </Box>
  );
}

export default withAuthenticationRequired(GenieDrawer, {
  // Show a spinner while the user is being redirected to the login page
  onRedirecting: () => <GenieSpinner />,
  claimCheck: (user) => {
    return true;
  },
  returnTo: () => {
    return "/storefront";
  },
});
